import { CredentialType } from "./enums";

/**
 * Closed value sets for credential fields that the type registry declares as
 * bare strings.
 *
 * [provider-credentials-type-schemas.md](../../../docs/provider-credentials-type-schemas.md)
 * specifies these per type — a licence type, and a standing per credential kind
 * — but the registry was implemented with `z.string()`, so nothing enforced or
 * even recorded them. That went unnoticed because nothing wrote these fields:
 * the legacy state-licence field carries only state, number and expiry. The
 * moment a form exists, the gap becomes a free-text box feeding a promoted,
 * queryable column.
 *
 * Values are the documented spellings, not this repo's kebab-case enum
 * convention, wherever the spelling has to match data that already exists or
 * arrives from an external system. Reconciling a migrated `Active` with a
 * locally-invented `active` is not something a filter can do after the fact.
 */

/**
 * The discipline a licence was issued in — what the state board licensed the
 * person *as*.
 *
 * These are the `code` values of `healthcare_professional_type`, NOT a separate
 * list. That table is already the canonical vocabulary for a person's
 * profession and is what credential requirements resolve against, so a licence
 * type drawn from anywhere else would leave a nurse practitioner's professional
 * type and their licence type as unrelated strings that no join reconciles.
 *
 * Mirrored here rather than fetched because a form descriptor is static data and
 * this is a slow-moving reference set;
 * `healthcare-professional-type-codes.test.ts` fails if the table and this enum
 * ever diverge.
 *
 * The Assured import vocabulary (type-schemas §1) is a *foreign* spelling of the
 * same concept and is mapped on import — notably degree and abbreviation values
 * such as `MD`, `PHD`, and `ATC`.
 */
export enum LicenseType {
  ATHLETIC_TRAINER = "ATHLETIC_TRAINER",
  AUDIOLOGIST = "AUDIOLOGIST",
  BEHAVIOR_ANALYST = "BEHAVIOR_ANALYST",
  CAA = "CAA",
  CHIROPRACTOR = "CHIROPRACTOR",
  CLINICAL_SOCIAL_WORKER = "CLINICAL_SOCIAL_WORKER",
  CNM = "CNM",
  CNS = "CNS",
  COUNSELOR = "COUNSELOR",
  CRNA = "CRNA",
  DENTIST = "DENTIST",
  DIETITIAN = "DIETITIAN",
  LPN_LVN = "LPN_LVN",
  MASSAGE_THERAPIST = "MASSAGE_THERAPIST",
  MFT = "MFT",
  NP = "NP",
  OPTOMETRIST = "OPTOMETRIST",
  OT = "OT",
  OTHER = "OTHER",
  PA = "PA",
  PHYSICIAN = "PHYSICIAN",
  PODIATRIST = "PODIATRIST",
  PSYCHOLOGIST = "PSYCHOLOGIST",
  PT = "PT",
  RAD_TECH = "RAD_TECH",
  RESPIRATORY_THERAPIST = "RESPIRATORY_THERAPIST",
  RN = "RN",
  SONOGRAPHER = "SONOGRAPHER",
  SLP = "SLP",
  SURGICAL_ASSISTANT = "SURGICAL_ASSISTANT",
}

/**
 * Human-readable name per code — the `name` column of the same table.
 *
 * A picker showing "RAD_TECH" and "PSYCHOLOGIST" is less readable; the name is
 * what a person recognises. Consumers order by label themselves — enum
 * declaration order is by code, which reads as scrambled to someone scanning
 * names.
 */
export const LICENSE_TYPE_LABELS: Readonly<Record<LicenseType, string>> = {
  [LicenseType.ATHLETIC_TRAINER]: "Athletic Trainer",
  [LicenseType.AUDIOLOGIST]: "Audiologist",
  [LicenseType.BEHAVIOR_ANALYST]: "Behavior Analyst",
  [LicenseType.CAA]: "Anesthesiologist Assistant",
  [LicenseType.CHIROPRACTOR]: "Chiropractor",
  [LicenseType.CLINICAL_SOCIAL_WORKER]: "Clinical Social Worker",
  [LicenseType.CNM]: "Certified Nurse-Midwife",
  [LicenseType.CNS]: "Clinical Nurse Specialist",
  [LicenseType.COUNSELOR]: "Counselor",
  [LicenseType.CRNA]: "Certified Registered Nurse Anesthetist",
  [LicenseType.DENTIST]: "Dentist",
  [LicenseType.DIETITIAN]: "Dietitian",
  [LicenseType.LPN_LVN]: "Licensed Practical/Vocational Nurse",
  [LicenseType.MASSAGE_THERAPIST]: "Massage Therapist",
  [LicenseType.MFT]: "Marriage and Family Therapist",
  [LicenseType.NP]: "Nurse Practitioner",
  [LicenseType.OPTOMETRIST]: "Optometrist",
  [LicenseType.OT]: "Occupational Therapist",
  [LicenseType.OTHER]: "Other",
  [LicenseType.PA]: "Physician Assistant",
  [LicenseType.PHYSICIAN]: "Physician",
  [LicenseType.PODIATRIST]: "Podiatrist",
  [LicenseType.PSYCHOLOGIST]: "Psychologist",
  [LicenseType.PT]: "Physical Therapist",
  [LicenseType.RAD_TECH]: "Radiologic Technologist",
  [LicenseType.RESPIRATORY_THERAPIST]: "Respiratory Therapist",
  [LicenseType.RN]: "Registered Nurse",
  [LicenseType.SONOGRAPHER]: "Diagnostic Medical Sonographer",
  [LicenseType.SLP]: "Speech-Language Pathologist",
  [LicenseType.SURGICAL_ASSISTANT]: "Surgical Assistant",
};

/**
 * Assured's licence-type spellings that do not exist as disciplines here.
 *
 * Degrees, not professions — a psychologist licensed with a PsyD and one with a
 * PhD practise the same discipline. Kept as an explicit mapping so an import
 * cannot quietly invent a professional type, and so the reason is recorded
 * rather than rediscovered.
 */
export const FOREIGN_LICENSE_TYPE_ALIASES: Readonly<
  Record<string, LicenseType>
> = {
  AA: LicenseType.CAA,
  APN: LicenseType.NP,
  ATC: LicenseType.ATHLETIC_TRAINER,
  AUD: LicenseType.AUDIOLOGIST,
  BCBA: LicenseType.BEHAVIOR_ANALYST,
  CADC: LicenseType.COUNSELOR,
  DC: LicenseType.CHIROPRACTOR,
  DDS: LicenseType.DENTIST,
  DMD: LicenseType.DENTIST,
  DNP: LicenseType.NP,
  DO: LicenseType.PHYSICIAN,
  DPM: LicenseType.PODIATRIST,
  LCSW: LicenseType.CLINICAL_SOCIAL_WORKER,
  LMFT: LicenseType.MFT,
  LMHC: LicenseType.COUNSELOR,
  LMT: LicenseType.MASSAGE_THERAPIST,
  LPC: LicenseType.COUNSELOR,
  LPN: LicenseType.LPN_LVN,
  LP: LicenseType.PSYCHOLOGIST,
  MD: LicenseType.PHYSICIAN,
  NEUROPSY: LicenseType.PSYCHOLOGIST,
  OD: LicenseType.OPTOMETRIST,
  PHD: LicenseType.PSYCHOLOGIST,
  PSY: LicenseType.PSYCHOLOGIST,
  PSYD: LicenseType.PSYCHOLOGIST,
  RD: LicenseType.DIETITIAN,
  RDMS: LicenseType.SONOGRAPHER,
  RRT: LicenseType.RESPIRATORY_THERAPIST,
  RT: LicenseType.RAD_TECH,
  SA: LicenseType.SURGICAL_ASSISTANT,
};

/** type-schemas §1. */
export enum StateLicenseStatus {
  Active = "active",
  Probation = "probation",
  Suspended = "suspended",
  Inactive = "inactive",
  Deceased = "deceased",
  NotRenewed = "not-renewed",
  Cancelled = "cancelled",
}

/** type-schemas §2; §3 (`cds`) shares it verbatim. */
export enum RegistrationStatus {
  Active = "active",
  Expired = "expired",
  Surrendered = "surrendered",
  Revoked = "revoked",
}

/** type-schemas §4 — Assured's `is_board_certified` boolean, lifted to a status. */
export enum BoardCertificationStatus {
  Certified = "certified",
  NotCertified = "not-certified",
  Lifetime = "lifetime",
}

/** type-schemas §14. */
export enum MalpracticeInsuranceStatus {
  Active = "active",
  Expired = "expired",
  Cancelled = "cancelled",
}

/** type-schemas §8 — the kinds of life-support certification we track. */
export enum LifeSupportKind {
  Bls = "bls",
  Acls = "acls",
  Pals = "pals",
  Nrp = "nrp",
  Nihss = "nihss",
  Other = "other",
}

/** type-schemas §8. */
export enum LifeSupportStatus {
  Active = "active",
  Expired = "expired",
}

/** type-schemas §9. */
export enum HealthScreeningStatus {
  Passed = "passed",
  Failed = "failed",
  PendingResult = "pending-result",
}

/** type-schemas §11. */
export enum PassportStatus {
  Active = "active",
  Expired = "expired",
  Cancelled = "cancelled",
}

/** type-schemas §12. */
export enum DriversLicenseStatus {
  Active = "active",
  Expired = "expired",
  Suspended = "suspended",
  Revoked = "revoked",
}

/** type-schemas §20. */
export enum BackgroundCheckStatus {
  Clear = "clear",
  Consider = "consider",
  Pending = "pending",
}

/** type-schemas §22. */
export enum CollaborativeAgreementStatus {
  Active = "active",
  Terminated = "terminated",
  Expired = "expired",
}

/**
 * Standing of a hospital privilege.
 *
 * Capitalized against the convention on purpose: `legacy-transforms.ts` copies
 * `privilege_status` verbatim from the legacy field, whose JSON schema fixes
 * these four spellings, and migrated rows already hold them. type-schemas §17
 * notes Assured sends free text here — that arrives through import, not through
 * a form, so a form still offers the vocabulary we actually store.
 */
export enum HospitalPrivilegeStatus {
  Active = "Active",
  Pending = "Pending",
  Suspended = "Suspended",
  Terminated = "Terminated",
}

/**
 * Allowed values for the promoted `credential_version.status` column, per type.
 *
 * A type absent from this map has no documented standing, and the write path
 * rejects any status for it — an unconstrained value in a queryable column is
 * worse than no value, because a filter cannot tell it apart from a typo.
 */
export const CREDENTIAL_STATUS_VOCABULARY: Partial<
  Record<CredentialType, readonly string[]>
> = {
  [CredentialType.StateLicense]: Object.values(StateLicenseStatus),
  [CredentialType.Dea]: Object.values(RegistrationStatus),
  [CredentialType.Cds]: Object.values(RegistrationStatus),
  [CredentialType.BoardCertification]: Object.values(BoardCertificationStatus),
  [CredentialType.MalpracticeInsurance]: Object.values(
    MalpracticeInsuranceStatus,
  ),
  [CredentialType.CollaborativeAgreement]: Object.values(
    CollaborativeAgreementStatus,
  ),
  [CredentialType.HospitalAffiliation]: Object.values(HospitalPrivilegeStatus),
  [CredentialType.LifeSupport]: Object.values(LifeSupportStatus),
  [CredentialType.HealthScreening]: Object.values(HealthScreeningStatus),
  [CredentialType.Passport]: Object.values(PassportStatus),
  [CredentialType.DriversLicense]: Object.values(DriversLicenseStatus),
  [CredentialType.BackgroundCheck]: Object.values(BackgroundCheckStatus),
};

export function credentialStatusVocabulary(
  type: CredentialType,
): readonly string[] | undefined {
  return CREDENTIAL_STATUS_VOCABULARY[type];
}
